/*
	This form is for Mediaocean Facebook connections.  For Flashtalking Facebook connections, see FacebookVideo
*/

import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { Field, FormSection, getFormValues } from "redux-form";

import { ReduxControls } from "rhombus-react";
import DynamicAssetSelect from "social/DynamicAssetSelect";

import { ASSET_TYPES, CREATIVE_ASSET_TYPES, SOCIAL_AD_TYPES } from "utils/constants";
import { SOCIAL_WIZARD } from "widgets/forms/list";
import usePlacementDynamicUpdate from "hooks/usePlacementDynamicUpdate";
import { noop } from "utils/common";
import { CREATIVE_NAME_MAX_LENGTH } from "widgets/forms/validators/Creatives";
import { FACEBOOK_VIDEO_CONSTRAINTS } from "social/validators/facebook";
import { PAC_FORM_TYPES, UNEDITABLE_DYNAMIC_VARIABLE_MESSAGE } from "social/constants";
import {
	dynamicMapStringToOption,
	dynamicNormalizeOptionToString,
	nameWithPlacementPrefix,
	nonDynamicFields
} from "social/utils";
import PACController from "social/PACController";
import DynamicInput from "social/DynamicInput";
import DynamicSelect from "social/DynamicSelect";
import { isDynamic } from "social/DynamicCreativeToggle";
import { assetType } from "social/BuildMethodRadio";
import { tellSentry } from "utils/Rave";
import { FB_CTA_OPTIONS } from "social/constants/CTA";
import { PAC_SELECT_DISABLED_VERSIONS_MESSAGE } from "../constants/placements";

const {
	MAX_VIDEO_MB,
	VIDEO_ACCEPT,
	MIN_VIDEO_SEC,
	MAX_VIDEO_MIN,
	MAX_LENGTH_LANDING_PAGE,
	THUMBNAIL_ACCEPT,
	MAX_THUMBNAIL_MB,
} = FACEBOOK_VIDEO_CONSTRAINTS;

const FILE_SIZE_REQUIREMENT = { name: "File Size:", description: `${MAX_VIDEO_MB}MB Maximum` };
const FILE_TYPE_REQUIREMENT = { name: "File Types:", description: VIDEO_ACCEPT };
const FILE_DURATION_REQUIREMENT = { name: "Duration:", description: `Between ${MIN_VIDEO_SEC} sec and ${MAX_VIDEO_MIN} min` };

const FACEBOOK_VIDEO_REQUIREMENTS = {
	[PAC_FORM_TYPES.DEFAULT] : [
		FILE_SIZE_REQUIREMENT,
		FILE_TYPE_REQUIREMENT,
		FILE_DURATION_REQUIREMENT,
		{ name: "Dimensions:", description: "120x120 pixels or larger required, 1080x1080 or larger recommended" }
	],
	[PAC_FORM_TYPES.HORIZONTAL] : [
		FILE_SIZE_REQUIREMENT,
		FILE_TYPE_REQUIREMENT,
		FILE_DURATION_REQUIREMENT,
		{ name: "Dimensions:", description: "120x120 pixels or larger required, 1920x1080 or larger recommended" }
	],
	[PAC_FORM_TYPES.VERTICAL]: [
		FILE_SIZE_REQUIREMENT,
		FILE_TYPE_REQUIREMENT,
		FILE_DURATION_REQUIREMENT,
		{ name: "Dimensions:", description: "120x120 pixels or larger required, 1080x1920 or larger recommended" }
	],
	[PAC_FORM_TYPES.SQUARE]: [
		FILE_SIZE_REQUIREMENT,
		FILE_TYPE_REQUIREMENT,
		FILE_DURATION_REQUIREMENT,
		{ name: "Dimensions:", description: "120x120 pixels or larger required, 1080x1080 or larger recommended" }
	],
	[PAC_FORM_TYPES.REELS]: [
		FILE_SIZE_REQUIREMENT,
		FILE_TYPE_REQUIREMENT,
		FILE_DURATION_REQUIREMENT,
		{ name: "Dimensions:", description: "120x120 pixels or larger required, 1080x1920 or larger recommended" }
	],
};

const FACEBOOK_THUMBNAIL_REQUIREMENTS = [
	{
		name: "File Size:",
		description: `<= ${MAX_THUMBNAIL_MB}MB`,
	},
	{
		name: "File Types:",
		description: THUMBNAIL_ACCEPT,
	},
	{
		name: "Dimensions:",
		description: "254x133 pixels or larger required, Aspect ratio should match video",
	},
];

export function getFacebookVideoFields(placementType) {
	return {
		isDynamic,
		assetType,
		video: {
			name: "video",
			label: "Video",
			className: "full-width",
			assetType: ASSET_TYPES.VIDEO,
			component: DynamicAssetSelect,
			defaultDynVarName: nameWithPlacementPrefix("video"),
			requirements: FACEBOOK_VIDEO_REQUIREMENTS[placementType],
			required: true,
			accept: VIDEO_ACCEPT,
			tip: {
				direction: "top",
				description: "At least 500x889 for better placement compatibility",
			},
		},
		thumbnail: {
			name: "thumbnail",
			label: "Thumbnail",
			className: "full-width",
			assetType: ASSET_TYPES.IMAGE,
			component: DynamicAssetSelect,
			defaultDynVarName: nameWithPlacementPrefix("video_thumbnail"),
			requirements: FACEBOOK_THUMBNAIL_REQUIREMENTS,
			accept: THUMBNAIL_ACCEPT,
			tip: {
				direction: "top",
				description: "Video and thumbnail image must have the same aspect ratio",
			},
		},
		name: {
			name: "name",
			label: "Creative Name",
			className: "full-width",
			placeholder: "Enter a creative name",
			component: ReduxControls.Input,
			charMax: CREATIVE_NAME_MAX_LENGTH,
			showCharCount: true,
			showPristineErrors: true,
			required: true,
		},
		headline: {
			name: "headline",
			label: "Headline",
			className: "full-width",
			placeholder: "Enter a short headline",
			tip: {
				description: "May get truncated after 40 characters",
				direction: "right",
			},
			component: DynamicInput,
			defaultDynVarName: nameWithPlacementPrefix("headline"),
		},
		primaryText: {
			name: "primaryText",
			label: "Primary Text",
			className: "full-width",
			placeholder: "Tell people what your creative is about",
			tip: {
				description: "May get truncated after 125 characters",
				direction: "right",
			},
			component: DynamicInput,
			defaultDynVarName: nameWithPlacementPrefix("copy"),
		},
		description: {
			name: "description",
			label: "Description",
			className: "full-width",
			placeholder: "Include additional details",
			tip: {
				description: "May get truncated after 30 characters",
				direction: "right",
			},
			component: DynamicInput,
			defaultDynVarName: nameWithPlacementPrefix("description"),
		},
		landingPage: {
			name: "landingPage",
			label: "Landing Page",
			className: "full-width",
			placeholder: "https://www.example.com",
			component: DynamicInput,
			defaultDynVarName: nameWithPlacementPrefix("clickTag1"),
			charMax: MAX_LENGTH_LANDING_PAGE,
			// showCharCount: true,
			required: true,
		},
		displayLink: {
			name: "displayLink",
			label: "Display Link",
			className: "half-width",
			placeholder: "Enter link text to be displayed",
			component: DynamicInput,
			defaultDynVarName: nameWithPlacementPrefix("display_link"),
		},
		cta: {
			name: "cta",
			label: "CTA",
			className: "half-width",
			placeholder: "Enter Call to Action Text",
			component: DynamicSelect,
			defaultDynVarName: nameWithPlacementPrefix("cta"),
			options: FB_CTA_OPTIONS,
			normalize: dynamicNormalizeOptionToString,
			format: dynamicMapStringToOption,
			isClearable: false,
			menuPlacement: "top",
			required: true,
			onMenuScrollToBottom: noop,
			variableNameInputTip: "Edit variable name",
		},
		sponsorPageId: {
			name: "sponsorPageId",
			label: "Sponsor Page ID",
			className: "full-width",
			placeholder: "Enter Sponsor Facebook Page ID",
			component: ReduxControls.Input,
			...nonDynamicFields(),
		},
	};
}

function fieldGenerator(formProps, customGenerator) {
	const {
		navigationActions,
		libraryId,
		isViewOnly,
		showPristineErrors,
		placementType,
		creativeAssetType,
		onChangeDefaultIsDynamic,
		doVersionsExist,
	} = formProps;

	const facebookVideoFields = useMemo(() => {
		return getFacebookVideoFields(placementType);
	}, [placementType, creativeAssetType]);

	return (name) => {
		const fieldProps = facebookVideoFields[name];
		const isFieldDynamicEnabled = !!fieldProps.defaultDynVarName; /*
			defaultDynVarName is present on any dynamic field, regardless of the input type
			so it's a useful shorthand.
		*/
		const isDefaultPlacement = placementType === PAC_FORM_TYPES.DEFAULT;

		if (!fieldProps) { // Sanity check
			console.warn(`Unknown field name in FacebookVideoMO: ${name}`);
			tellSentry(`Unknown field name in FacebookVideoMO: ${name}`);
			return null;
		}

		let extraProps = {};
		let customProps = {};
		if (customGenerator) {
			customProps = customGenerator(formProps);
		}

		if(fieldProps.name === "video" || fieldProps.name === "thumbnail") {
			const isAdStudio = creativeAssetType === CREATIVE_ASSET_TYPES.ADSTUDIO;
			extraProps.navigationActions = navigationActions;
			extraProps.libraryId = libraryId;
			extraProps.isAdStudio = isAdStudio;
			extraProps.required = !isAdStudio && fieldProps.required;

			if(isFieldDynamicEnabled){
				if(isDefaultPlacement){
					extraProps.onChange = onChangeDefaultIsDynamic;
				} else {
					// Only the default placement is allowed to toggle dynamic status on asset fields.
					extraProps.dynamicToggleDisabled = true;
				}
			}
		}

		extraProps.disabled = isViewOnly;

		// If versions exist besides default, then dynamic toggle should be disabled
		if (doVersionsExist) {
			extraProps.dynamicToggleDisabled = true;
			extraProps.variableNameInputDisabled = true;
			if (fieldProps.name !== "cta") {
				extraProps.variableNameInputTip = UNEDITABLE_DYNAMIC_VARIABLE_MESSAGE;
			}
		}

		if (extraProps.disabled && fieldProps.placeholder) {
			// We set this on extraProps, because we don't want to mutate the fieldProps consts
			extraProps.placeholder = '';
		}

		extraProps.showPristineErrors = showPristineErrors;

		return	(
			<Field
				key={`${placementType}-${fieldProps.name}`}
				{...fieldProps}
				{...extraProps}
				{...customProps}
			/>
		);
	};
}

function FacebookVideoMODefault(props) {
	const placementType = PAC_FORM_TYPES.DEFAULT;
	const generateFields = fieldGenerator({ ...props, placementType });
	const fields = Object.keys(getFacebookVideoFields(placementType)).map(generateFields);

	return (
		<div className="fields">
			{fields}
		</div>
	);
}

FacebookVideoMODefault.displayName = "FacebookVideoMODefaultForm";

export default function FacebookVideoMO(props){
	const { isViewOnly, doVersionsExist } = props;
	const formValues = useSelector(getFormValues(SOCIAL_WIZARD)) || {};
	const { assetType } = formValues[SOCIAL_AD_TYPES.FACEBOOK_VIDEO_MO] || {};
	const onChangeDefaultIsDynamic = usePlacementDynamicUpdate(SOCIAL_AD_TYPES.FACEBOOK_VIDEO_MO);

	const defaultForm = (
		<FacebookVideoMODefault
			{...props}
			creativeAssetType={assetType}
			onChangeDefaultIsDynamic={onChangeDefaultIsDynamic}
		/>
	);

	let selectProps = {};
	let headerProps = {};

	if (doVersionsExist) {
		selectProps.searchTooltip = PAC_SELECT_DISABLED_VERSIONS_MESSAGE;
		selectProps.buttonTooltip = PAC_SELECT_DISABLED_VERSIONS_MESSAGE;
		headerProps.noAssetCustomizeTooltip = PAC_SELECT_DISABLED_VERSIONS_MESSAGE;
		headerProps.customizeAssetTooltip = PAC_SELECT_DISABLED_VERSIONS_MESSAGE;
	}

	return (
		<FormSection name={SOCIAL_AD_TYPES.FACEBOOK_VIDEO_MO}>
			<PACController
				{...props}
				adType={SOCIAL_AD_TYPES.FACEBOOK_VIDEO_MO}
				defaultForm={defaultForm}
				defaultFieldGenerator={fieldGenerator}
				creativeAssetType={assetType}
				onChangeDefaultIsDynamic={onChangeDefaultIsDynamic}
				isViewOnly={isViewOnly}
				selectProps={selectProps}
				headerProps={headerProps}
			/>
		</FormSection>
	);
}

FacebookVideoMO.displayName = "FacebookVideoMOForm";

export const PRIVATE = {
	FacebookVideoMODefault,
};
